// @flow

import React from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import Ajv from "ajv";

import * as sharedSelectors from "@modules/shared/selectors";
import * as serviceSelectors from "@modules/service/selectors";
import actionCreator from "@modules/service/actionCreator";
import { Button, Col, Row, View } from "@components";
import type { StepProps } from "@components";
import { AlertRecipientsForm } from "@modules/service/components";
import { AlertBespokeRecipientsForm } from "@modules/service/components";

import type { SpecialContactType } from "@modules/shared/reducer/special-contact-types/types";
import type {
  Alert,
  AlertSpecialContactType,
  AlertBespokeRecipient,
} from "@modules/service/reducer/alerts/types";
import { FULL_NOTIFICATION_CONTACTS } from "@modules/service/constants";

type StepData = $Exact<{
  specialContactTypes: Array<Object>,
  bespokeRecipients: Array<Object>,
}>;

type OwnProps = $Exact<{
  editing: boolean,
  clientId: number,
}>;

type PropsFromState = $Exact<{
  schema: ?Object,
  draft: ?Alert,
  specialContactTypes: Array<SpecialContactType>,
}>;

type PropsFromDispatch = $Exact<{
  setDraft: typeof actionCreator.SetAlertDraft,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...StepProps,
  ...PropsFromLocalize,
}>;

export class StepRecipients extends React.Component<Props> {
  validateRecipients: *;
  validateBespokeRecipients: *;

  componentDidMount() {
    this.validateRecipients = new Ajv().compile(
      (this.props.schema || {}).properties.specialContactTypes,
    );
    this.validateBespokeRecipients = new Ajv().compile(
      (this.props.schema || {}).properties.bespokeRecipients,
    );
  }

  componentDidUpdate(prevProps: Props) {
    if (this.props.schema && this.props.schema !== prevProps.schema) {
      this.validateRecipients = new Ajv().compile(
        (this.props.schema || {}).properties.specialContactTypes,
      );
      this.validateBespokeRecipients = new Ajv().compile(
        (this.props.schema || {}).properties.bespokeRecipients,
      );
    }
  }

  get data(): StepData {
    const _data = (this.props.draft || {}).content;
    const notificationContactSelected = ((_data || {}).details || {}).notifyEmployeeContacts;
    let specialContactTypes = (_data || {}).specialContactTypes || [];

    if (specialContactTypes && notificationContactSelected) {
      specialContactTypes = [
        ...[
          {
            id: 0,
            name: this.props.t(FULL_NOTIFICATION_CONTACTS),
          },
        ],
        ...specialContactTypes,
      ];
    }

    return {
      specialContactTypes,
      bespokeRecipients: (_data || {}).bespokeRecipients || [],
    };
  }

  isValid = (data: Object) => {
    if (!this.validateRecipients || !this.validateBespokeRecipients) {
      return false;
    }

    const { specialContactTypes, bespokeRecipients } = data;
    return (
      (!specialContactTypes || this.validateRecipients(specialContactTypes)) &&
      (!bespokeRecipients || this.validateBespokeRecipients(bespokeRecipients))
    );
  };

  updateDefaultRecipientChanges = (data: Array<AlertSpecialContactType>) => {
    const notificationContactSelected = data.find(
      i => i.name === this.props.t(FULL_NOTIFICATION_CONTACTS),
    );
    const specialContactTypes = data.filter(
      i => i.name !== this.props.t(FULL_NOTIFICATION_CONTACTS),
    );

    this.props.setDraft({
      ...this.props.draft,
      content: {
        ...this.props.draft?.content,
        details: {
          ...this.props.draft?.content.details,
          notifyEmployeeContacts: !!notificationContactSelected,
        },
        specialContactTypes,
      },
    });
  };

  updateBespokeRecipientsChanges = (data: ?Array<AlertBespokeRecipient>) => {
    this.props.setDraft({
      ...this.props.draft,
      content: {
        ...this.props.draft?.content,
        bespokeRecipients: data,
      },
    });
  };

  handleSubmit = () => {
    if (this.props.handleSubmit) {
      const specialContactTypes = this.data.specialContactTypes
        ? this.data.specialContactTypes.filter(
            i => i.name !== this.props.t(FULL_NOTIFICATION_CONTACTS),
          )
        : null;

      this.props.setDraft({
        ...this.props.draft,
        specialContactTypes,
        bespokeRecipients: this.data.bespokeRecipients,
      });
      this.props.handleSubmit({}, true);
    }
  };

  get specialContactTypes() {
    return [
      ...[
        {
          id: 0,
          name: this.props.t(FULL_NOTIFICATION_CONTACTS),
          isEditable: false,
          key: FULL_NOTIFICATION_CONTACTS,
        },
      ],
      ...this.props.specialContactTypes,
    ];
  }

  render() {
    if (!this.props.editable) {
      return <div />;
    }

    return (
      <View gap={4}>
        <AlertRecipientsForm
          data={this.data.specialContactTypes}
          options={this.specialContactTypes}
          onChange={this.updateDefaultRecipientChanges}
        />
        <AlertBespokeRecipientsForm
          clientId={this.props.clientId}
          data={this.data}
          updateBespokeRecipients={this.updateBespokeRecipientsChanges}
        />
        {this.props.editing ? null : (
          <div className="w-100 d-flex justify-content-end mt-5 mb-3">
            <Button
              materialIcon="fast_forward"
              onClick={this.handleSubmit}
              disabled={!this.isValid(this.data)}
            >
              {this.props.t("NEXT")}
            </Button>
          </div>
        )}
      </View>
    );
  }
}

const mapStateToProps = (state): PropsFromState => {
  return {
    schema: serviceSelectors.getAlertSchema(state),
    draft: serviceSelectors.getAlertDraft(state),
    specialContactTypes: sharedSelectors.getSpecialContactTypes(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  setDraft: actionCreator.SetAlertDraft,
};

export default compose(localize(), connect(mapStateToProps, mapDispatchToProps))(StepRecipients);
